"use client";

import React, {
  startTransition,
  useActionState,
  useEffect,
  useState,
} from "react";
import ConfirmModal from "../ui/ConfirmModal";
import { VIEWING_INITIAL_STATE } from "@/services/form.service";
import { CaseData, CMSData } from "@/cms/models";
import { submitViewingForm } from "@/actions/viewing-form";
import { formatDate } from "@/utils/helper";
import { showFormDateTimeFields } from "@/utils/form-scheduling";
import FormDateTimeFields from "@/components/ui/inputs/FormDateTimeFields";
import TextInput from "../ui/inputs/TextInput";
import TextAreaInput from "../ui/inputs/TextAreaInput";
import FormLayout from "../ui/inputs/FormLayout";
import { useReCaptcha } from "next-recaptcha-v3";

const ViewingForm: React.FC<{
  caseData: CaseData;
  cmsData: CMSData;
}> = ({ cmsData, caseData }) => {
  const showDateTimeFields = showFormDateTimeFields(cmsData.forms, "viewing");
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalidCaptchaOpen, setIsInvalidCaptchaOpen] = useState(false);
  const [formState, formAction, isPending] = useActionState(
    (prevState: any, formData: FormData) => {
      formData.append("caseId", caseData!.id!);
      return submitViewingForm(prevState, formData).then((newState) => {
        if (newState.isSuccess) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "viewingform_submitted",
          });
          window?.pageVisitTracker?.track("ViewingFormSubmit", {
            caseId: caseData.id,
          });
        }

        return newState;
      });
    },
    VIEWING_INITIAL_STATE,
  );
  useEffect(() => {
    if (!("recaptchaIsValid" in formState)) {
      return;
    }

    console.log("Recaptcha:", {
      enabled: formState.recaptchaIsEnabled,
      validToken: formState.recaptchaIsValid,
    });

    if (!formState.recaptchaIsValid) {
      setIsInvalidCaptchaOpen(true);
    } else {
      setIsOpen(formState.isSuccess ?? false);
    }
  }, [formState]);
  const {
    firstName,
    lastName,
    address,
    zip,
    city,
    phone,
    email,
    message,
    time,
    date,
    confirmedDate,
    confirmedTime,
  } = formState?.data || {};

  const { executeRecaptcha } = useReCaptcha();
  const submit = (payload: FormData) => {
    (async () => {
      try {
        const token = await executeRecaptcha("AssessmentForm");
        payload.append("recaptchaToken", token);
      } catch (error) {
        console.log("[ReCaptcha Error]:", error);
        payload.delete("recaptchaToken");
      } finally {
        startTransition(() => {
          formAction(payload);
        });
      }
    })();
  };

  return (
    <div className="">
      <ConfirmModal
        description="Får du brug for at ændre, justere eller aflyse din bestilling, kan du kontakte os på:"
        title="Du har anmodet om en fremvisning"
        setOpen={(isOpen) => setIsOpen(isOpen)}
        isOpen={isOpen}
        contactInfo={{
          phone: caseData?.resourceGroup?.formattedPhone ?? "",
          email: caseData?.resourceGroup?.email ?? "",
        }}
      >
        <div className="flex flex-col">
          <div className="homepage-text-sm mb-3">
            {showDateTimeFields && (
              <div>
                Dato:{" "}
                {[formatDate(confirmedDate), confirmedTime]
                  .filter((x) => !!x)
                  .join(" klokken: ")}
              </div>
            )}
            <div>{caseData!.advertisementAddressOrDisplayName}</div>
          </div>
          <div>
            {showDateTimeFields
              ? "Vi bestræber os på at imødekomme dit ønske. Kan det ønskede tidspunkt ikke lade sig gøre, finder vi et nyt sammen med dig."
              : "Vi kontakter dig for at aftale en fremvisning."}
          </div>
        </div>
      </ConfirmModal>
      <ConfirmModal
        modalType="error"
        description="Prøv venligst igen. Hvis du fortsætter med at opleve denne fejl så kontakt venligst support."
        title="Din forespørgsel blev blokeret"
        setOpen={(isOpen) => setIsInvalidCaptchaOpen(isOpen)}
        isOpen={isInvalidCaptchaOpen}
      />
      <div className="flex flex-col gap-4 flex-1 w-full">
        <FormLayout
          submitLabel="Bestil"
          featureFlags={cmsData?.featureFlags}
          isPending={isPending}
          formAction={submit}
        >
          <FormDateTimeFields
            cmsData={cmsData}
            form="viewing"
            date={date}
            time={time}
            errors={formState?.zodErrors}
          />
          <TextInput
            cmsData={cmsData}
            name="firstName"
            label="Fornavn"
            defaultValue={firstName}
            errors={formState?.zodErrors?.firstName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            name="lastName"
            label="Efternavn"
            defaultValue={lastName}
            errors={formState?.zodErrors?.lastName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-6"
            name="address"
            label="Adresse"
            defaultValue={address}
            errors={formState?.zodErrors?.address}
            required={cmsData?.forms.addressRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-2"
            name="zip"
            label="Postnr"
            defaultValue={zip}
            errors={formState?.zodErrors?.zip}
            required={cmsData?.forms.zipRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-4"
            name="city"
            label="By"
            defaultValue={city}
            errors={formState?.zodErrors?.city}
            required={cmsData?.forms.cityRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="phone"
            label="Telefonnummer"
            defaultValue={phone}
            errors={formState?.zodErrors?.phone}
            required={cmsData?.forms.phoneRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="email"
            label="E-mail"
            defaultValue={email}
            errors={formState?.zodErrors?.email}
            required={cmsData?.forms.emailRequired}
          />
          <TextAreaInput
            cmsData={cmsData}
            name="message"
            label="Besked"
            defaultValue={message}
            errors={formState?.zodErrors?.message}
            required={cmsData?.forms.messageRequired}
          />
        </FormLayout>
      </div>
    </div>
  );
};

export default ViewingForm;
